import React, { useCallback, useEffect, useRef, useState } from 'react';
import UserAvatar from './UserAvatar';
import UserProfileCard from './UserProfileCard';

/**
 * A person's avatar that opens their profile card on hover.
 *
 * ONE implementation, used by chat, boards and teams. It existed twice before
 * this — inline in TaskAssigneeChip and click-triggered in ChatMessageView —
 * and a third copy in Teams was the point at which "the same card everywhere"
 * stopped being true by accident and had to become true on purpose.
 *
 * The timers are the whole reason this is not four lines. The card is portalled
 * to <body>, so moving the pointer towards it leaves the avatar's subtree
 * immediately: without a close grace it vanishes under the cursor, and without
 * an open delay dragging across a member list strobes.
 */

// Long enough that crossing a list does not flash cards, short enough to feel
// like a tooltip rather than a wait.
const OPEN_MS = 350;
// The pointer's journey from the avatar to the card.
const CLOSE_MS = 120;

export default function MemberAvatar({
  userId,
  name,
  email,
  size = 32,
  className = '',
  rounded = 'circle',
}) {
  const [rect, setRect] = useState(null);
  const anchorRef = useRef(null);
  const openTimer = useRef(null);
  const closeTimer = useRef(null);

  const clearTimers = useCallback(() => {
    clearTimeout(openTimer.current);
    clearTimeout(closeTimer.current);
  }, []);

  const scheduleOpen = useCallback(() => {
    clearTimers();
    if (userId == null) return;
    openTimer.current = setTimeout(() => {
      const r = anchorRef.current?.getBoundingClientRect();
      if (r) setRect(r);
    }, OPEN_MS);
  }, [userId, clearTimers]);

  const scheduleClose = useCallback(() => {
    clearTimers();
    closeTimer.current = setTimeout(() => setRect(null), CLOSE_MS);
  }, [clearTimers]);

  const closeNow = useCallback(() => {
    clearTimers();
    setRect(null);
  }, [clearTimers]);

  useEffect(() => clearTimers, [clearTimers]);

  return (
    <span
      ref={anchorRef}
      className={`member-avatar member-avatar--${rounded} ${className}`.trim()}
      onMouseEnter={scheduleOpen}
      onMouseLeave={scheduleClose}
    >
      <UserAvatar userId={userId} name={name} size={size} />
      {rect && (
        <UserProfileCard
          userId={userId}
          name={name}
          email={email}
          anchorRect={rect}
          onClose={closeNow}
          // The card has to keep itself open while the pointer is ON it —
          // it is portalled away from this element, so its own mouseenter is
          // the only signal that the pointer did not simply leave.
          onMouseEnter={clearTimers}
          onMouseLeave={scheduleClose}
        />
      )}
    </span>
  );
}
